import { z } from 'zod'
import { nonEmpty, optionalText, uuid } from './common.ts'
import { patientFields } from './patient.ts'

export const FILE_KINDS = [
  'photo', 'signature', 'companion_signature', 'thumb',
  'aadhaar_front', 'aadhaar_back',
  // A product image belongs to no patient, so it gets a storage path on
  // the product row rather than a patient_files record.
  'product',
] as const

export const FILE_KIND_LABEL: Record<string, string> = {
  photo: 'Patient photo',
  aadhaar_front: 'Aadhaar — front',
  aadhaar_back: 'Aadhaar — back',
  signature: 'Signature of patient',
  companion_signature: 'Signature of companion',
  thumb: 'Thumb impression',
}
export type FileKind = (typeof FILE_KINDS)[number]

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
export const MAX_FILE_BYTES = 5 * 1024 * 1024

export const signUpload = z.object({
  kind: z.enum(FILE_KINDS),
  mimeType: z.enum(IMAGE_TYPES),
  sizeBytes: z.number().int().positive().max(MAX_FILE_BYTES),
})
export type SignUpload = z.infer<typeof signUpload>

export const fileRef = z.object({
  fileId: uuid,
  kind: z.enum(FILE_KINDS),
  mimeType: z.enum(IMAGE_TYPES),
  sizeBytes: z.number().int().positive().max(MAX_FILE_BYTES),
})

// Step 2. Weight is optional; the rest are not -- a register entry without
// a pulse or an oxygen reading is an incomplete clinical record.
export const measurementFields = z.object({
  weightKg: z.number().positive().max(500).optional().nullable(),
  bpSystolic: z.number().int().min(40).max(300),
  bpDiastolic: z.number().int().min(20).max(200),
  pulse: z.number().int().min(20).max(250),
  spo2: z.number().int().min(50).max(100),
})
  // Both numbers are individually plausible when they are entered the wrong
  // way round -- 80/120 passes every range check and is silently wrong in a
  // clinical record. Systolic is always the higher of the two.
  .refine(m => m.bpSystolic > m.bpDiastolic, {
    message: 'Systolic must be higher than diastolic — check the two are not swapped',
    path: ['bpSystolic'],
  })
export type MeasurementFields = z.infer<typeof measurementFields>

export const OPIATE_UNITS = ['kg', 'g'] as const
export const OPIATE_SUBSTANCES = ['poppy', 'opium', 'tola'] as const
export type OpiateSubstance = (typeof OPIATE_SUBSTANCES)[number]

export const SUBSTANCE_LABEL: Record<OpiateSubstance, string> = {
  poppy: 'Poppy',
  opium: 'Opium',
  tola: 'Tola',
}

// Step 3. The amount and unit together ARE the monthly consumption:
// "1.5 kg per month".
export const drugFields = z.object({
  opiateAmount: z.number().nonnegative().max(999999).optional().nullable(),
  opiateUnit: z.enum(OPIATE_UNITS).optional().nullable(),
  opiateSubstance: z.enum(OPIATE_SUBSTANCES).optional().nullable(),
  tablets: optionalText(400),
  addictionYears: z.number().int().min(0).max(120).optional().nullable(),
  addictionMonths: z.number().int().min(0).max(11).optional().nullable(),
})

// Only remarks is collected. The referral, registration-number, hospital,
// ticket and previous-treatment fields from the printed form were dropped
// as unnecessary; their columns remain in the database so the decision can
// be reversed without a destructive migration.
export const recordFields = z.object({
  remarks: optionalText(1000),
})

export const createRegistration = z.object({
  // Both ids come from the client, so a retry after a lost response
  // replays them and returns the existing record instead of registering
  // the same patient onto the statutory register twice.
  id: uuid,
  patientId: uuid,
  patient: patientFields.extend({ fullName: nonEmpty(160) }),
  measurement: measurementFields,
  drug: drugFields,
  record: recordFields,
  declarationVersion: nonEmpty(60),
  files: z.array(fileRef).max(6).default([]),
})
export type CreateRegistration = z.infer<typeof createRegistration>

// Everything a register entry holds, editable in one transaction: the
// patient, the entry itself, and the reading taken at registration.
// Separate PATCHes would leave the record half-updated if one failed.
export const updateRegistrationFull = z.object({
  patient: patientFields.extend({ fullName: nonEmpty(160) }).partial().optional(),
  drug: drugFields.partial().optional(),
  record: recordFields.partial().optional(),
  measurement: measurementFields.optional(),
  // Images may be replaced after registration -- a blurred card photograph
  // is only discovered once someone tries to read it.
  files: z.array(fileRef).max(6).optional(),
  // Both versions are checked. A registration can be edited while another
  // terminal is editing the same patient, and neither should silently win.
  patientVersion: z.number().int().positive(),
  registrationVersion: z.number().int().positive(),
})
export type UpdateRegistrationFull = z.infer<typeof updateRegistrationFull>
